import type { Feedback, FeedbackEnvironment, FeedbackStatus, FeedbackType } from '@/shared/domain'
import { buildQueryString, type HttpGet } from '@/shared/infra/http'
import type { ListUserFeedback } from '@/modules/feedback/use-cases'

const FEEDBACK_TYPES: readonly FeedbackType[] = ['bug', 'idea', 'ui-ux', 'performance', 'other']
const FEEDBACK_STATUSES: readonly FeedbackStatus[] = ['submitted', 'reviewing', 'resolved']

export type EnvironmentWire = {
  browser?: string | null
  platform?: string | null
  screenResolution?: string | null
  viewportSize?: string | null
  userAgent?: string | null
  timezone?: string | null
}

export type FeedbackRowWire = {
  id?: string | null
  userId?: string | null
  orgId?: string | null
  orgType?: string | null
  personaRole?: string | null
  feedbackType?: string | null
  feature?: string | null
  message?: string | null
  pageUrl?: string | null
  appVersion?: string | null
  environment?: EnvironmentWire | null
  status?: string | null
  source?: string | null
  attachmentUrl?: string | null
  createdAt?: string | null
  updatedAt?: string | null
}

type ListWire = {
  feedback?: FeedbackRowWire[] | null
}

function toFeedbackType(value: string | null | undefined): FeedbackType {
  return FEEDBACK_TYPES.includes(value as FeedbackType) ? (value as FeedbackType) : 'other'
}

function toFeedbackStatus(value: string | null | undefined): FeedbackStatus {
  return FEEDBACK_STATUSES.includes(value as FeedbackStatus) ? (value as FeedbackStatus) : 'submitted'
}

function toEnvironment(wire: EnvironmentWire | null | undefined): FeedbackEnvironment | undefined {
  if (!wire) return undefined
  return {
    browser: wire.browser ?? '',
    platform: wire.platform ?? '',
    screenResolution: wire.screenResolution ?? '',
    viewportSize: wire.viewportSize ?? '',
    userAgent: wire.userAgent ?? '',
    timezone: wire.timezone ?? '',
  }
}

export function toFeedback(row: FeedbackRowWire): Feedback {
  return {
    id: row.id ?? '',
    userId: row.userId ?? '',
    orgId: row.orgId ?? undefined,
    orgType: row.orgType ?? undefined,
    personaRole: row.personaRole ?? undefined,
    feedbackType: toFeedbackType(row.feedbackType),
    feature: row.feature ?? '',
    message: row.message ?? '',
    pageUrl: row.pageUrl ?? '',
    appVersion: row.appVersion ?? undefined,
    environment: toEnvironment(row.environment),
    status: toFeedbackStatus(row.status),
    source: row.source === 'web' || row.source === 'mobile' ? row.source : undefined,
    attachmentUrl: row.attachmentUrl ?? undefined,
    createdAt: row.createdAt ?? '',
    updatedAt: row.updatedAt ?? '',
  }
}

export class SupabaseListUserFeedbackRepository {
  constructor(private readonly http: HttpGet) {}

  execute: ListUserFeedback = async (params) => {
    const query = buildQueryString({ limit: params?.limit, offset: params?.offset })
    const response = await this.http.get<ListWire>({ url: `/feedback${query}` })
    return (response?.feedback ?? []).map(toFeedback)
  }
}
